import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { API_CONFIG } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore } from '../stores/user';
import {
  PROFILE_VERTICALS,
  PROFILE_VERTICAL_CONFIGS,
  getProfileVerticalFromAppContext,
  getRatingContextForVertical,
  normalizeProfileVertical,
  type ProfileVertical,
} from '@photofeeler/shared';
import { showToast } from '../components/Toast';

interface UploadState {
  file?: File;
  preselectedContext?: string;
  preselectedVertical?: ProfileVertical;
  retakeOf?: string;
}
interface UploadResponse {
  objectKey: string;
  contentType: string;
  sizeBytes: number;
}

export function Upload() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, session } = useAuth();
  const { credits, setCredits } = useUserStore();
  const file = (location.state as UploadState | null)?.file;
  const retakeOf = (location.state as UploadState | null)?.retakeOf;
  const initialVertical = normalizeProfileVertical(
    (location.state as UploadState | null)?.preselectedVertical ||
      getProfileVerticalFromAppContext((location.state as UploadState | null)?.preselectedContext)
  );
  const [vertical, setVertical] = useState<ProfileVertical>(initialVertical);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const isDemo = new URLSearchParams(location.search).get('demo') === 'true';

  useEffect(() => {
    if (!file && !isDemo) {
      navigate('/app');
      return;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file, isDemo, navigate]);

  useEffect(() => {
    setVertical(initialVertical);
  }, [initialVertical]);

  useEffect(() => {
    if (!session?.access_token || isDemo) return;
    let cancelled = false;

    fetch(`${API_CONFIG.RATE_URL}/user/credits`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!cancelled && data && typeof data.credits === 'number') {
          setCredits(Math.max(0, data.credits));
        }
      })
      .catch(() => {
        // Keep the locally cached value if the refresh fails.
      });

    return () => {
      cancelled = true;
    };
  }, [isDemo, session?.access_token, setCredits]);

  const uploadMutation = useMutation({
    mutationFn: async (f: File) => {
      const h: Record<string, string> = {};
      if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${API_CONFIG.UPLOAD_URL}/upload`, {
        method: 'POST',
        headers: h,
        body: fd,
      });
      if (!res.ok) {
        if (res.status === 401) {
          navigate('/');
          throw new Error('Your session expired. Please sign in again to continue.');
        }
        const e = await res.json().catch(() => ({}));
        throw new Error((e as { error?: string }).error || 'We couldn\u0027t upload your photo. Check your connection and try again.');
      }
      return res.json() as Promise<UploadResponse>;
    },
  });

  const rateMutation = useMutation({
    mutationFn: async ({ objectKey, context: ctx }: { objectKey: string; context: string }) => {
      const h: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
      const res = await fetch(`${API_CONFIG.RATE_URL}/rate`, {
        method: 'POST',
        headers: h,
        body: JSON.stringify({ objectKey, context: ctx }),
      });
      if (!res.ok) {
        if (res.status === 401) {
          navigate('/');
          throw new Error('Your session expired. Please sign in again to continue.');
        }
        if (res.status === 402) {
          import('../lib/analytics').then(({ trackCreditsExhausted }) => trackCreditsExhausted());
          throw new Error('You\u0027re out of credits. Grab more to keep testing photos.');
        }
        throw new Error('Something went wrong starting the analysis. Your credit was not charged \u2014 please try again.');
      }
      return res.json() as Promise<{ photoId: string; credits?: number }>;
    },
  });

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      setProgress('Uploading...');
      const up = await uploadMutation.mutateAsync(file);
      const ctx = getRatingContextForVertical(vertical);
      import('../lib/analytics').then(({ trackPhotoUploaded }) => trackPhotoUploaded(ctx));
      setProgress('Starting analysis...');
      const rate = await rateMutation.mutateAsync({
        objectKey: up.objectKey,
        context: ctx,
      });
      import('../lib/analytics').then(({ trackAnalysisStarted }) => trackAnalysisStarted(ctx, rate.photoId));
      if (rate.credits !== undefined) setCredits(rate.credits);
      else if (!isDemo) setCredits(Math.max(0, credits - 1));
      navigate(`/results/${rate.photoId}`, { state: retakeOf ? { retakeOf } : undefined });
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Something went wrong. Please try again.', 'error');
    } finally {
      setUploading(false);
      setProgress('');
    }
  };

  if (!isAuthenticated || (!file && !isDemo)) return null;

  return (
    <div className="relative overflow-hidden pt-24 pb-16 px-5 sm:px-8 max-w-5xl mx-auto animate-fade-in">
      <div className="page-orb left-[-5rem] top-28 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-6rem] top-40 h-80 w-80 bg-info/10" />

      <button
        onClick={() => navigate('/app')}
        className="btn-ghost text-xs mb-6"
      >
        ← Back
      </button>

      <div className="page-hero-panel mb-8">
        <div className="premium-kicker mb-3">Photo upload</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink mb-3" style={{ fontWeight: 600 }}>
          Analyze photo
        </h1>
        <p className="hero-copy max-w-2xl">
          Confirm the context, check the preview, and start a multi-model review of this photo.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{PROFILE_VERTICAL_CONFIGS[vertical].label}</div>
            <div className="text-xs text-ink-faint mt-1">Active workspace</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{isDemo ? 'Free' : '1 credit'}</div>
            <div className="text-xs text-ink-faint mt-1">Cost per analysis</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{credits}</div>
            <div className="text-xs text-ink-faint mt-1">Credits available</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="card lift-hover">
          <h2 className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-4">
            Preview
          </h2>
          {preview ? (
            <>
              <img
                src={preview}
                alt="Preview"
                className="w-full rounded-lg object-contain max-h-80"
              />
              {file && (
                <div className="flex gap-3 mt-3 text-[11px] text-ink-faint font-sans">
                  <span>{file.name}</span>
                  <span>·</span>
                  <span>{(file.size / 1048576).toFixed(1)} MB</span>
                  <span>·</span>
                  <span>{file.type.split('/')[1]?.toUpperCase()}</span>
                </div>
              )}
            </>
          ) : (
            <div className="aspect-square bg-cream-warm rounded-lg flex items-center justify-center">
              <span className="text-ink-faint text-sm">Demo</span>
            </div>
          )}
        </div>

        <div className="card-rose lift-hover">
          <h2 className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-4">
            Context
          </h2>
          <div className="space-y-2">
            {PROFILE_VERTICALS.map(profileVertical => {
              const option = PROFILE_VERTICAL_CONFIGS[profileVertical];
              return (
              <label
                key={option.id}
                className={`flex items-start p-4 rounded-lg border cursor-pointer transition-all duration-300 ${vertical === option.id ? 'border-rose/30 bg-rose-faint' : 'border-surface-border hover:border-ink/10'}`}
              >
                <input
                  type="radio"
                  name="context"
                  value={option.id}
                  aria-label={option.label}
                  checked={vertical === option.id}
                  onChange={e => setVertical(e.target.value as ProfileVertical)}
                  className="mt-0.5 mr-3 accent-[#D4736A]"
                />
                <div>
                  <div className="text-sm font-sans font-medium text-ink">{option.label}</div>
                  <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                    {option.description}
                  </div>
                </div>
              </label>
              );
            })}
          </div>
          <div className="mt-6 pt-5 border-t border-surface-border">
            <div className="flex justify-between mb-4 text-sm">
              <span className="text-ink-faint">Cost</span>
              <span className="font-medium text-ink">{isDemo ? 'Free' : '1 credit'}</span>
            </div>
            <div className="glass-note mb-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-faint mb-2">Before you run it</div>
              <div className="text-sm text-ink-muted leading-relaxed" style={{ fontWeight: 300 }}>
                {vertical === 'dating'
                  ? 'This photo will be scored for dating app impact — attractiveness, warmth, and first-impression chemistry. Results in about 15 seconds.'
                  : vertical === 'linkedin'
                    ? 'This photo will be scored for professional impression — competence, trust, and recruiter appeal. Results in about 15 seconds.'
                    : 'Use the context that matches where this photo will appear. Results are usually ready in about 15 seconds. If analysis fails, your credit is returned automatically.'}
              </div>
            </div>
            {!isDemo && credits < 1 && (
              <div className="text-xs text-rose bg-rose-faint p-3 rounded-lg mb-4">
                Not enough credits.{' '}
                <button onClick={() => navigate('/purchase')} className="font-medium underline">
                  Buy more
                </button>
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={uploading || (!isDemo && credits < 1)}
              className="btn-rose w-full"
            >
              {uploading ? progress || 'Processing...' : 'Analyze photo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
